import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import {
  DropdownEntry,
  DropdownGroup,
} from '@design/components/dropdown-menu/dropdown-group/DropdownGroup';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import {
  type AppTableFeatures,
  RevampTable,
  TableCell,
  TableHeader,
  TableHeaderIcons,
} from '@design/components/table';
import {
  Column,
  flexRender,
  type RowData,
  SortDirection,
  Table,
} from '@tanstack/react-table';
import { NoDataFound } from '@terminal/components/no-data-found/NoDataFound';
import clsx from 'clsx';
import { t } from 'i18next';
import styles from './ThemeEarningsTable.module.scss';

export type ThemeEarningsHeaderAlignment = 'start' | 'end' | 'center';
export type ThemeEarningsCellAlignment = 'start' | 'end' | 'left' | 'center';

export interface ThemeEarningsColumnLayout {
  headerClassName?: string;
  cellClassName?: string;
  headerAlignment: ThemeEarningsHeaderAlignment;
  cellAlignment: ThemeEarningsCellAlignment;
  groupBoundary?: string;
}

interface ThemeEarningsTableProps<TData extends RowData> {
  table: Table<AppTableFeatures, TData>;
  columnLayout: Record<string, ThemeEarningsColumnLayout>;
  onRowClick: (row: TData) => void;
  hasMoreRows?: boolean;
  onResetFilters?: () => void;
  hasAppliedFilters?: boolean;
  dataTestId: string;
}

const getSortDropdownItems = <TData extends RowData>(
  column: Column<AppTableFeatures, TData, unknown>,
  sortOrder: false | SortDirection
): DropdownEntry[] => {
  const enabledKeys = sortOrder
    ? ['sortUp', 'sortDown', 'clearSort']
    : ['sortUp', 'sortDown'];

  const items: DropdownEntry[] = [
    {
      label: t('common:table.dropdown_ascending'),
      iconName: 'SortUp',
      key: 'sortUp',
      onClick: () => column.toggleSorting(false),
    },
    {
      label: t('common:table.dropdown_descending'),
      iconName: 'SortDown',
      key: 'sortDown',
      onClick: () => column.toggleSorting(true),
    },
    {
      label: t('common:table.dropdown_clearSort'),
      iconName: 'ClearSort',
      key: 'clearSort',
      onClick: () => column.clearSorting(),
    },
  ];

  return items.filter(item => enabledKeys.includes(item.key));
};

export const ThemeEarningsTable = <TData extends RowData>({
  table,
  columnLayout,
  onRowClick,
  hasMoreRows,
  onResetFilters,
  hasAppliedFilters,
  dataTestId,
}: ThemeEarningsTableProps<TData>) => {
  const hasRows = table.getRowModel().rows.length > 0 || !!hasMoreRows;

  return (
    <RevampTable
      className={clsx({ [styles.empty]: !hasRows })}
      data-testid={dataTestId}
      withBodyRowHover={hasRows}
    >
      {hasRows && (
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const layout = columnLayout[header.column.id];
                const alignment = layout?.headerAlignment ?? 'start';
                const sortOrder = header.column.getIsSorted();
                const isSortable = header.column.getCanSort();
                const headerContent = flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                );

                if (!isSortable) {
                  return (
                    <TableHeader
                      key={header.id}
                      className={layout?.headerClassName}
                      data-group-boundary={layout?.groupBoundary}
                      alignment={alignment}
                      colSpan={header.colSpan}
                      isHoverEnabled={false}
                    >
                      {headerContent}
                    </TableHeader>
                  );
                }

                return (
                  <DropdownMenu
                    key={header.id}
                    triggerNode={
                      <TableHeader
                        className={layout?.headerClassName}
                        data-group-boundary={layout?.groupBoundary}
                        alignment={alignment}
                        colSpan={header.colSpan}
                        isHoverEnabled
                      >
                        <TableHeaderIcons
                          sortDirection={sortOrder}
                          alignment={alignment}
                        >
                          {headerContent}
                        </TableHeaderIcons>
                      </TableHeader>
                    }
                  >
                    <DropdownGroup
                      items={getSortDropdownItems(header.column, sortOrder)}
                    />
                  </DropdownMenu>
                );
              })}
            </tr>
          ))}
        </thead>
      )}
      <tbody>
        {table.getRowModel().rows.map(row => (
          <tr key={row.id} onClick={() => onRowClick(row.original)}>
            {row.getVisibleCells().map(cell => {
              const layout = columnLayout[cell.column.id];

              return (
                <TableCell
                  key={cell.id}
                  className={layout?.cellClassName}
                  data-group-boundary={layout?.groupBoundary}
                  alignment={layout?.cellAlignment ?? 'start'}
                  isNumber={false}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              );
            })}
          </tr>
        ))}
        {hasMoreRows && (
          <tr data-loading-row data-testid={`${dataTestId}-loading-row`}>
            {table.getVisibleLeafColumns().map(column => {
              const layout = columnLayout[column.id];

              return (
                <TableCell
                  key={column.id}
                  className={layout?.cellClassName}
                  data-group-boundary={layout?.groupBoundary}
                  alignment={layout?.cellAlignment ?? 'start'}
                  isNumber={false}
                >
                  <SkeletonLoader
                    areas={[
                      {
                        styles: {
                          width: '100%',
                          height: '20px',
                          borderRadius: '8px',
                        },
                      },
                    ]}
                  />
                </TableCell>
              );
            })}
          </tr>
        )}
        {!hasRows && (
          <tr data-testid={`${dataTestId}-empty-row`}>
            <TableCell
              className={styles.emptyCell}
              colspan={table.getVisibleLeafColumns().length}
              alignment="center"
              isNumber={false}
            >
              <NoDataFound
                bannerPath="/app/static/images/empty-state/radio-telescope.png"
                size="small"
                title={t('screener:emptyTitle')}
                {...(onResetFilters && hasAppliedFilters
                  ? {
                      buttonLabel: t('common:filters.resetAllFilters'),
                      buttonAction: onResetFilters,
                      buttonTestId: 'reset-all-filters-btn',
                    }
                  : {})}
              />
            </TableCell>
          </tr>
        )}
      </tbody>
    </RevampTable>
  );
};
